<template>
  <link rel="stylesheet" href="https://js.stripe.com/v3/css/stripe.css">

  <div class="flex h-auto bg-gray-900 text-white">
    <!-- Sidebar -->
    <aside class="w-64 h-auto bg-gray-800 p-6 flex flex-col space-y-6 shadow-lg bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700">
      <div class="flex items-center justify-center space-x-3">
       <img src="@/assets/logo.svg" alt="Vultric Logo" class="h-20">
      </div>
      <Navbar></Navbar>
    </aside>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col">
      <!-- Top Navbar -->
      <Header></Header>

      <!-- Billing Page Content -->
      <main class="p-6 flex-1">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- Current Balance -->
          <div class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 
                      rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition">
            <h4 class="text-md font-semibold">Current Balance</h4>
            <p class="text-2xl font-bold" :class="billing.balance.credit > 0 ? 'text-green-400' : 'text-red-400'">
              ${{ billing.balance.credit }}
            </p>
            <button v-if="billing.balance.stripeCustomerId" @click="toggleAddCreditPopup" class="mt-3 bg-blue-500 p-2 rounded-lg hover:bg-blue-600">
              Add Funds
            </button>
          </div>

          <!-- Next Invoice -->
          <div class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 
                      rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition">
            <h4 class="text-md font-semibold">Next Billing Cycle</h4>
            <p class="text-lg">Due: {{ formatDate(billing.due_date) }}</p>
            <p class="text-xl font-bold text-red-400">${{ parseFloat(billing.moneydue).toFixed(2) }}</p>
          </div>

          <!-- Auto Refill or Add Payment Method -->
          <div class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 
                      rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition">
            <h4 class="text-md font-semibold">Payment Method</h4>
            <div v-if="!billing.balance.stripeCustomerId">
              <p>You need to add a payment method to order.</p>
              <button @click="toggleAddPayment" class="mt-3 bg-blue-500 p-2 rounded-lg hover:bg-blue-600">
                Add Payment Method
              </button>
            </div>
            <div v-else>
              <p>Your saved payment method ends in: {{ billing.paymentMethodLast4 }}</p>
              <button @click="toggleAddPayment" class="mt-3 bg-blue-500 p-2 rounded-lg hover:bg-blue-600">
                Update
              </button>
            </div>
          </div>
        </div>

        <!-- Services Using Credit -->
        <div class="p-6 mt-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 
        rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition">
          <h3 class="text-lg font-semibold mb-4">Active Services</h3>
          <table class="w-full text-left">
            <thead>
              <tr class="border-b border-gray-700">
                <th class="p-2">Service</th>
                <th class="p-2">Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="service in services" :key="service.id" class="border-b border-gray-700">
                <td class="p-3">{{ service.servername }}</td>
                <td class="p-3">${{ service.cost }}/mo</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Invoice History -->
        <div class="p-6 mt-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 
        rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition">
          <h3 class="text-lg font-semibold mb-4">Invoice History</h3>
          <table class="w-full text-left">
            <thead>
              <tr class="border-b border-gray-700">
                <th class="p-2">Date</th>
                <th class="p-2">Invoice #</th>
                <th class="p-2">Amount</th>
                <th class="p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="invoice in invoices" :key="invoice.date" class="border-b border-gray-700 hover:bg-gray-700 transition cursor-pointer" @click="$router.push(`/invoice/${invoice.id}`)">
                <td class="p-3">{{ formatDate(invoice.date) }}</td>
                <td class="p-3">#{{ invoice.id }}</td>
                <td class="p-3">${{ invoice.amount }}</td>
                <td class="p-3" :class="{ 'text-green-400': invoice.status === 'Paid', 'text-red-400': invoice.status !== 'Paid' }">
                  {{ invoice.status }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </main>
    </div>
  </div>
  <!-- Add Credit Popup -->
  <div v-if="showAddCreditPopup" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
    <div class="bg-gray-800 p-6 rounded-lg shadow-lg w-96">
      <h3 class="text-lg font-semibold mb-4">Add Funds</h3>

       <!-- Input for amount -->
       <input v-model="addAmount" type="number" placeholder="Amount to add ($)" class="w-full p-2 rounded bg-gray-700 mb-3">

      <div class="flex justify-end space-x-2">
        <button @click="toggleAddCreditPopup" class="bg-gray-600 p-2 rounded-lg">Cancel</button>
        <button @click="handleStripePayment(addAmount)" class="bg-blue-500 p-2 rounded-lg hover:bg-blue-600">Add Funds</button>
      </div>
    </div>
  </div>

    <!-- Add Payment Popup -->
    <div v-if="showAddPayment" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
    <div class="bg-gray-800 p-6 rounded-lg shadow-lg w-96">
      <h3 class="text-lg font-semibold mb-4">Add Payment Method</h3>

      <!-- Stripe Card element -->
      <div id="card-element" class="w-full p-2 rounded bg-gray-700 mb-3"></div>

      <div class="flex justify-end space-x-2">
        <button @click="toggleAddPayment" class="bg-gray-600 p-2 rounded-lg">Cancel</button>
        <button @click="saveCardDetails" class="bg-blue-500 p-2 rounded-lg hover:bg-blue-600">Add Payment Method</button>
      </div>
    </div>
  </div>

  <!-- Toast Notifications -->
  <ToastManager ref="toastManager" />
</template>

<script>
import Navbar from '@/components/nav.vue';
import Header from '@/components/header.vue';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import ToastManager from '@/components/ToastManager.vue';

const stripePromise = loadStripe('pk_live_51HnCLVBARnItI0M3AtLBMUwhJ8xTWa6J0u92boIt5zKVl5P5giNS4Tiiq5lEL0DYebRzRU2rn0wI348ltRs42Rxx00ttTLE1tl');

export default {
  name: 'BillingPage',
  data() {
    return {
      showAddCreditPopup: false,
      showAddPayment: false,
      addAmount: '',
      cardElement: null,
      billing: {
        balance: 0,
        due_date: '',
        next_invoice_amount: 0,
        auto_refill: false,
        refill_amount: 0,
        stripeCustomerId: null,
        paymentMethodLast4: null,
      },
      invoices: [],
      services: [],
    };
  },
  methods: {
    toggleAddCreditPopup() {
      this.showAddCreditPopup = !this.showAddCreditPopup;
    },
    toggleAddPayment() {
    this.showAddPayment = !this.showAddPayment;
    if (this.showAddPayment) {
      this.initStripeElements(); // Mount Stripe Elements when popup is shown
    }
  },
    async fetchBillingData() {
      try {
        const response = await axios.get('https://api.syniac.com:3000/api/billing/', { withCredentials: true });
        this.billing = { ...response.data };
        this.invoices = response.data.invoices;
        this.services = response.data.services;
      } catch (error) {
        console.error("Error fetching billing data:", error);
      }
    },
    async saveCardDetails() {
        try {
          const stripe = await stripePromise;
          const elements = stripe.elements();

          // Check if card element is already created
          if (!this.cardElement) {
            const style = {
              base: {
                color: "#fff",
                fontSize: "16px",
                fontFamily: "Arial, sans-serif",
                "::placeholder": {
                  color: "#ddd",
                },
              },
            };
            this.cardElement = elements.create('card', { style });

            this.$nextTick(() => {
              const cardElementContainer = document.getElementById('card-element');
              if (cardElementContainer && this.cardElement) {
                this.cardElement.mount(cardElementContainer);
              } else {
                console.error("Card element container not found or card element not initialized properly!");
              }
            });
          }

          const { token, error } = await stripe.createToken(this.cardElement);

          if (error) {
            console.error(error);
            this.$refs.toastManager.addToast('Error creating payment method!', 'error');
          } else {
            const response = await axios.post('https://api.syniac.com:3000/api/billing/save-payment-method', { token }, { withCredentials: true });
            if (response.data.success) {
              this.billing.stripeCustomerId = response.data.stripeCustomerId;
              this.$refs.toastManager.addToast('Payment method added successfully!', 'success');
              this.fetchBillingData();
              this.showAddPayment = false;
            }
          }
        } catch (error) {
          console.error("Error handling card details:", error);
        }
      },
      async initStripeElements() {
        const stripe = await stripePromise;
        const elements = stripe.elements();

        const style = {
          base: {
            color: "#fff", 
            fontSize: "16px", 
            fontFamily: "Arial, sans-serif",
            "::placeholder": {
              color: "#ddd",
            },
          },
        };

        this.cardElement = elements.create('card', { style });

        this.$nextTick(() => {
          const cardElementContainer = document.getElementById('card-element');
          if (cardElementContainer && this.cardElement) {
            this.cardElement.mount(cardElementContainer);
          } else {
            console.error("Card element container not found or card element not initialized properly!");
          }
        });
      },
    async handleStripePayment(amount) {
      try {
        const response = await axios.post('https://api.syniac.com:3000/api/billing/charge-customer', { amount }, { withCredentials: true });
        if (response.data.success) {
          this.$refs.toastManager.addToast('Payment successful!', 'success');
          await axios.post('https://api.syniac.com:3000/api/billing/add-credit', { amount: this.addAmount }, { withCredentials: true });
          this.fetchBillingData();
        } else {
          this.$refs.toastManager.addToast('Payment failed!', 'error');
        }
      } catch (error) {
        console.error("Error handling payment:", error);
        this.$refs.toastManager.addToast('Payment failed!', 'error');
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString("en-US", { timeZone: "UTC" });
    },
    async fetchPaymentInfo() {
      try {
        const response = await axios.get('https://api.syniac.com:3000/api/billing/payment-method', { withCredentials: true });
          if (response.data.success) {
              this.billing.paymentMethodLast4 = response.data.last4; 
          } 
      } catch (error) {
          console.error('Error fetching payment method', error);
      }
    }
  },
  mounted() {
    this.fetchBillingData();
    this.fetchPaymentInfo();
  },
  components: { Navbar, Header, ToastManager }
};
</script>
